import React, { useState, useEffect, useRef } from "react";
import { Cpu, Code2, Wifi } from "lucide-react";
import { getSkills } from "../api/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

const CATS = [
  { key: "hardware", label: "Hardware", icon: <Cpu size={14} />, color: "#ffb700" },
  { key: "software", label: "Software", icon: <Code2 size={14} />, color: "#00e5ff" },
  { key: "networking", label: "Networking", icon: <Wifi size={14} />, color: "#ff0099" },
];

function SkillPill({ skill, color, index }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "14px 20px",
        borderRadius: 4,
        border: "1px solid",
        borderColor: hovered ? color : "rgba(255,255,255,0.07)",
        background: hovered ? `${color}0a` : "var(--card)",
        transition: "all 0.2s ease",
        cursor: "none",
        animation: `fadeUp 0.5s ease ${index * 0.05}s both`,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: 14, color: hovered ? "#f2f2ff" : "#c0c0d0", transition: "color 0.2s" }}>
          {skill.name}
        </span>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: hovered ? color : "#6a6a88", transition: "color 0.2s" }}>
          {skill.level}%
        </span>
      </div>
      <div style={{ height: 2, background: "rgba(255,255,255,0.05)", borderRadius: 1, overflow: "hidden" }}>
        <div style={{
          height: "100%",
          width: hovered ? `${skill.level}%` : "20%",
          background: hovered
            ? `linear-gradient(90deg, ${color}, ${color}88)`
            : "rgba(255,255,255,0.1)",
          borderRadius: 1,
          transition: "width 0.6s ease, background 0.3s ease",
        }} />
      </div>
      {skill.description && (
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#6a6a88", marginTop: 6 }}>{skill.description}</div>
      )}
    </div>
  );
}

export default function Skills() {
  const [skills, setSkills] = useState({ hardware: [], software: [], networking: [] });
  const ref = useRef();

  useEffect(() => { getSkills().then(setSkills).catch(console.error); }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { e.target.classList.add("visible"); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div style={{ padding: "120px 0", background: "var(--bg)", position: "relative", overflow: "hidden" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 32px" }}>

        {/* Header */}
        <div ref={ref} className="reveal" style={{ marginBottom: 64, position: "relative" }}>
          <div className="ghost-number" style={{ position: "absolute", left: -20, top: -60, zIndex: 0 }}>02</div>
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#6a6a88", letterSpacing: 3, marginBottom: 12 }}>KEAHLIAN</div>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(36px, 6vw, 72px)", fontWeight: 700, color: "#f2f2ff", margin: 0, letterSpacing: "-2px", lineHeight: 1 }}>
              Bidang<br />
              <span style={{ background: "linear-gradient(135deg,#00e5ff,#9b00ff,#ff0099,#ffb700,#00e5ff)", backgroundSize: "400% 400%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", animation: "holo-shift 5s ease infinite" }}>Spesialisasi</span>
            </h2>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="hardware">
          <TabsList style={{
            background: "var(--card)",
            border: "1px solid rgba(255,255,255,0.07)",
            padding: 4, borderRadius: 4,
            display: "inline-flex", gap: 2, marginBottom: 40,
          }}>
            {CATS.map(c => (
              <TabsTrigger key={c.key} value={c.key} style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 600, fontSize: 13,
                borderRadius: 3, padding: "8px 20px",
                display: "flex", alignItems: "center", gap: 7,
                letterSpacing: 0.5,
              }}>
                <span style={{ color: c.color }}>{c.icon}</span>
                {c.label.toUpperCase()}
              </TabsTrigger>
            ))}
          </TabsList>

          {CATS.map(cat => (
            <TabsContent key={cat.key} value={cat.key}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }} className="skills-cols">
                {(skills[cat.key] || []).map((skill, i) => (
                  <SkillPill key={skill.id} skill={skill} color={cat.color} index={i} />
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
